import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@arcadeai/design-system/components/ui/atoms/collapsible";
import { RequirementBadges } from "@arcadeai/design-system/components/ui/molecules/requirement-badges";
import { cn } from "@arcadeai/design-system/lib/utils";
import { getToolkitIcon } from "@arcadeai/design-system/metadata/toolkit-icons";
import type { ProcessedToolkit } from "@arcadeai/ui-kit/arcade/arcade";
import { ChevronDown, Wrench } from "lucide-react";
import { type ComponentType, memo, type ReactNode } from "react";

const ToolkitIconDisplay = ({
  Icon,
  name,
}: {
  Icon: ComponentType<{ className?: string }> | null;
  name: string;
}) => (
  <div className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-gradient-to-b from-neutral-200 to-neutral-100 shadow-sm ring-1 ring-black/5 dark:from-neutral-800 dark:to-neutral-900 dark:ring-white/5">
    {Icon ? (
      <Icon className="size-5 text-neutral-700 dark:text-neutral-200" />
    ) : (
      <span className="font-bold text-neutral-700 text-sm dark:text-neutral-200">
        {name.slice(0, 2).toUpperCase()}
      </span>
    )}
  </div>
);

type ToolkitCardProps = {
  toolkit: ProcessedToolkit;
  toolsCount: number;
  isExpanded?: boolean;
  onToggle: () => void;
  children?: ReactNode;
};

export const ToolkitCard = memo(function ToolkitCardComponent({
  toolkit,
  toolsCount,
  isExpanded = false,
  onToggle,
  children,
}: ToolkitCardProps) {
  const ToolkitIcon = getToolkitIcon(toolkit.metadata?.id ?? toolkit.name);
  const displayName = toolkit.metadata?.label ?? toolkit.name;
  const description = toolkit.description?.split("\n")[0];
  const hasRequirements =
    (toolkit.requirementsSummary?.secrets?.configured?.length ?? 0) > 0 ||
    (toolkit.requirementsSummary?.secrets?.pending?.length ?? 0) > 0 ||
    (toolkit.requirementsSummary?.oauth?.providers?.length ?? 0) > 0;

  return (
    <Collapsible
      className={cn(
        "group/card overflow-hidden rounded-xl border bg-card dark:border-white/5",
        isExpanded && "shadow-lg"
      )}
      onOpenChange={onToggle}
      open={isExpanded}
    >
      <CollapsibleTrigger
        className={cn(
          "flex w-full cursor-pointer items-center gap-3 px-3 py-3 text-left transition-colors sm:py-4",
          "group-data-closed/card:hover:bg-muted/30",
          "group-data-open/card:border-black/5 group-data-open/card:border-b group-data-open/card:dark:border-white/5"
        )}
      >
        <ToolkitIconDisplay Icon={ToolkitIcon} name={toolkit.name} />

        <div className="flex min-w-0 flex-1 flex-col gap-1">
          <div className="flex items-center gap-3">
            <h2 className="truncate font-semibold text-base text-foreground sm:text-lg">
              {displayName}
            </h2>
            {toolkit.version ? (
              <span className="hidden shrink-0 font-medium text-muted-foreground text-sm sm:inline">
                v{toolkit.version}
              </span>
            ) : null}
          </div>

          {description && !isExpanded ? (
            <p className="line-clamp-1 text-muted-foreground text-sm">
              {description}
            </p>
          ) : null}
        </div>

        <div className="flex shrink-0 items-center gap-1.5">
          {hasRequirements ? (
            <RequirementBadges
              badgeClassName="h-8 px-2.5 text-sm"
              requirementsSummary={toolkit.requirementsSummary}
            />
          ) : null}
          <span className="inline-flex h-8 shrink-0 items-center gap-1.5 rounded-md border border-transparent bg-muted/80 px-2.5 font-medium text-muted-foreground text-sm">
            <Wrench className="size-3" />
            <span>
              {`${toolsCount} ${toolsCount === 1 ? "tool" : "tools"}`}
            </span>
          </span>
          <div className="flex size-7 shrink-0 items-center justify-center">
            <ChevronDown className="size-5 text-muted-foreground transition-transform duration-200 group-data-open/card:rotate-180" />
          </div>
        </div>
      </CollapsibleTrigger>

      <CollapsibleContent className="overflow-hidden data-closed:animate-collapsible-up data-open:animate-collapsible-down">
        <div className="p-3">{children}</div>
      </CollapsibleContent>
    </Collapsible>
  );
});
